import React, { useState, useMemo } from 'react';

function GuidedNailsCombinedScreen({ pamperingAddOns, designAddOns, nailArtAddOns, selectedAddOns, onToggleAddOn, parentServiceId, serviceName, onSkip }) {
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [nailArtExpanded, setNailArtExpanded] = useState(false);
  const [expandedFinish, setExpandedFinish] = useState(null);

  const formatPrice = (price) => price > 0 ? `+$${price}` : 'Free';

  // Group nail art add-ons into levels
  const nailArtLevels = useMemo(() => {
    const levels = {};
    for (const addOn of nailArtAddOns) {
      const name = (addOn.name || '').toLowerCase();
      let level = null;
      let variant = null;

      if (name.includes('iii') || name.includes('detailed') || /\blevel\s*3\b/.test(name)) {
        level = 'detailed';
        variant = 'full';
      } else if (name.includes(' ii') || name.includes('intermediate') || /\blevel\s*2\b/.test(name)) {
        level = 'intermediate';
        variant = name.includes('accent') ? 'accent' : 'set';
      } else if (/\bi\b/.test(name) || name.includes('simple') || /\blevel\s*1\b/.test(name)) {
        level = 'simple';
        variant = name.includes('accent') ? 'accent' : 'set';
      }

      if (level) {
        if (!levels[level]) levels[level] = { addOns: [], accent: null, set: null, full: null };
        levels[level].addOns.push(addOn);
        if (variant === 'accent') levels[level].accent = addOn;
        else if (variant === 'set') levels[level].set = addOn;
        else levels[level].full = addOn;
      }
    }
    return levels;
  }, [nailArtAddOns]);

  const levelOrder = [
    { key: 'simple', label: 'SIMPLE' },
    { key: 'intermediate', label: 'INTERMEDIATE' },
    { key: 'detailed', label: 'DETAILED' },
  ];

  const selectedNailArt = nailArtAddOns.find(a =>
    selectedAddOns.some(s => s.id === a.id && s.parentServiceId === parentServiceId)
  );

  const hasGroupedLevels = Object.keys(nailArtLevels).length > 0;

  // Fallback descriptions for finishes
  const finishDescriptions = {
    'french': 'A timeless classic. Clean crisp tips in white or any colour of your choice.',
    'chrome': 'Achieve the iconic glazed-donut look. A high-shine, light reflecting finish.',
    'cat eye': 'A mesmerizing, light-catching effect using magnetic pigments. Shifts with every movement.',
  };

  function getFinishDescription(addOn) {
    if (addOn.description) return addOn.description;
    const name = (addOn.name || '').toLowerCase();
    for (const [key, desc] of Object.entries(finishDescriptions)) {
      if (name.includes(key)) return desc;
    }
    return '';
  }

  function handleDesignSelect(addOn) {
    onToggleAddOn(addOn, parentServiceId);
  }

  function handleLevelSelect(levelKey) {
    if (selectedNailArt) {
      onToggleAddOn(selectedNailArt, parentServiceId);
    }
    const level = nailArtLevels[levelKey];
    if (!level) return;
    setNailArtExpanded(true);
    if (levelKey === 'detailed' && (level.full || level.addOns.length === 1)) {
      setSelectedLevel('detailed');
      onToggleAddOn(level.full || level.addOns[0], parentServiceId);
    } else {
      setSelectedLevel(levelKey);
    }
  }

  function handleVariantSelect(addOn) {
    if (selectedNailArt && selectedNailArt.id !== addOn.id) {
      onToggleAddOn(selectedNailArt, parentServiceId);
    }
    const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
    if (!isSelected) {
      onToggleAddOn(addOn, parentServiceId);
    }
  }

  function handleChangeLevel() {
    if (selectedNailArt) {
      onToggleAddOn(selectedNailArt, parentServiceId);
    }
    setSelectedLevel(null);
  }

  return (
    <div className="guided-screen-content">
      {/* FINISHES — compact row of cards */}
      {designAddOns.length > 0 && (
        <div className="guided-section">
          <div className="guided-section-header">Finishes</div>
          <div className="guided-finish-grid">
            {designAddOns.map(addOn => {
              const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
              const isExpanded = expandedFinish === addOn.id;
              const desc = getFinishDescription(addOn);
              return (
                <div
                  key={addOn.id}
                  className={`guided-finish-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleDesignSelect(addOn)}
                >
                  <div className="guided-finish-name">{addOn.name}</div>
                  <div className="guided-finish-price">{formatPrice(addOn.price)}</div>
                  {desc && (
                    <div
                      className="guided-finish-learn"
                      onClick={(e) => { e.stopPropagation(); setExpandedFinish(isExpanded ? null : addOn.id); }}
                    >
                      {isExpanded ? 'Hide' : 'Learn more'}
                    </div>
                  )}
                  {isExpanded && desc && (
                    <div className="guided-finish-desc">{desc}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* NAIL ART — full-width button that expands */}
      {nailArtAddOns.length > 0 && hasGroupedLevels && (
        <div className="guided-section">
          {!nailArtExpanded && !selectedNailArt ? (
            <div
              className="guided-nail-art-bar"
              onClick={() => setNailArtExpanded(true)}
            >
              <span className="guided-nail-art-bar-name">Nail Art</span>
              <span className="guided-nail-art-bar-sub">See options</span>
            </div>
          ) : (
            <>
              {/* Level picker or selected level */}
              {!selectedLevel ? (
                <>
                  <div
                    className="guided-nail-art-bar open"
                    onClick={() => { setNailArtExpanded(false); }}
                  >
                    <span className="guided-nail-art-bar-name">Nail Art</span>
                    <span className="guided-nail-art-bar-sub">Close</span>
                  </div>
                  <div className="guided-nail-art-levels">
                    {levelOrder.map(({ key, label }) => {
                      const level = nailArtLevels[key];
                      if (!level) return null;
                      const prices = level.addOns.map(a => a.price).filter(p => p > 0);
                      const minPrice = Math.min(...prices);
                      const maxPrice = Math.max(...prices);
                      const priceStr = minPrice === maxPrice ? formatPrice(minPrice) : `${formatPrice(minPrice)}–$${maxPrice}`;
                      const durations = level.addOns.map(a => a.duration).filter(d => d > 0);
                      const minDur = Math.min(...durations);
                      const maxDur = Math.max(...durations);
                      const durStr = minDur === maxDur ? `+${minDur} min` : `+${minDur}–${maxDur} min`;
                      const desc = level.addOns[0]?.description || '';
                      return (
                        <div key={key} className="guided-nail-art-level" onClick={() => handleLevelSelect(key)}>
                          <div className="guided-nail-art-level-left">
                            <div className="guided-nail-art-level-name">{label}</div>
                            {desc && <div className="guided-nail-art-level-desc">{desc}</div>}
                          </div>
                          <div className="guided-nail-art-level-right">
                            <div className="guided-nail-art-level-price">{priceStr}</div>
                            {durations.length > 0 && <div className="guided-nail-art-level-time">{durStr}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : selectedLevel === 'detailed' ? (
                <div className="guided-yesno-collapsed selected-yes" onClick={handleChangeLevel}>
                  <span className="guided-yesno-collapsed-check">✓</span>
                  <span className="guided-yesno-collapsed-text">Detailed Nail Art</span>
                  <span className="guided-yesno-collapsed-change">Change</span>
                </div>
              ) : (
                <>
                  <div className="guided-yesno-collapsed selected-yes" onClick={handleChangeLevel}>
                    <span className="guided-yesno-collapsed-check">✓</span>
                    <span className="guided-yesno-collapsed-text">
                      {selectedLevel === 'simple' ? 'Simple' : 'Intermediate'} Nail Art
                    </span>
                    <span className="guided-yesno-collapsed-change">Change</span>
                  </div>
                  <div className="guided-section-title" style={{ marginTop: 10 }}>Accent or Full Set?</div>
                  <div className="guided-yesno-grid">
                    {nailArtLevels[selectedLevel].accent && (() => {
                      const addOn = nailArtLevels[selectedLevel].accent;
                      const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
                      return (
                        <button
                          className={`guided-yesno-btn ${isSelected ? 'guided-variant-selected' : ''}`}
                          onClick={() => handleVariantSelect(addOn)}
                        >
                          <div className="guided-yesno-label">Accent</div>
                          <div className="guided-yesno-sub">2–4 nails</div>
                          <div className="guided-variant-price">{formatPrice(addOn.price)}</div>
                          {addOn.duration > 0 && <div className="guided-card-time">+{addOn.duration} min</div>}
                        </button>
                      );
                    })()}
                    {nailArtLevels[selectedLevel].set && (() => {
                      const addOn = nailArtLevels[selectedLevel].set;
                      const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
                      return (
                        <button
                          className={`guided-yesno-btn ${isSelected ? 'guided-variant-selected' : ''}`}
                          onClick={() => handleVariantSelect(addOn)}
                        >
                          <div className="guided-yesno-label">Full Set</div>
                          <div className="guided-yesno-sub">5–10 nails</div>
                          <div className="guided-variant-price">{formatPrice(addOn.price)}</div>
                          {addOn.duration > 0 && <div className="guided-card-time">+{addOn.duration} min</div>}
                        </button>
                      );
                    })()}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* UPGRADES — pampering add-ons as list */}
      {pamperingAddOns.length > 0 && (
        <div className="guided-section">
          <div className="guided-section-header">Upgrades</div>
          <div className="guided-card-list">
            {pamperingAddOns.map(addOn => {
              const isSelected = selectedAddOns.some(s => s.id === addOn.id && s.parentServiceId === parentServiceId);
              return (
                <div
                  key={addOn.id}
                  className={`guided-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => onToggleAddOn(addOn, parentServiceId)}
                >
                  <div className="guided-card-left">
                    <div className="guided-card-name">{addOn.name}</div>
                    {addOn.description && <div className="guided-card-desc">{addOn.description}</div>}
                  </div>
                  <div className="guided-card-right">
                    <div className="guided-card-meta">
                      {addOn.duration > 0 && <div className="guided-card-time">{addOn.duration} Mins</div>}
                      <div className="guided-card-price">{formatPrice(addOn.price)}</div>
                    </div>
                    <div className="guided-card-check">{isSelected ? '✓' : ''}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button className="guided-no-addons-btn" onClick={onSkip}>
        NO ADD-ONS NEEDED
      </button>
    </div>
  );
}

export default GuidedNailsCombinedScreen;
